/**
 * F2-a: login owner (email + password + TOTP) verso api-cloud.
 *
 * Flusso TOTP: il primo tentativo invia solo email+password. Se il cloud
 * risponde 401 con error 'totp_required', mostriamo il campo codice e
 * ri-inviamo con totpCode. invalid_credentials / totp_invalid mappati a
 * messaggi chiari. On success -> setOwner + navigate /owner.
 *
 * Design: coerente con /login (palette warm boutique, logo HM arancio,
 * card arrotondata). Mobile-first, centrato.
 */
import { useMutation } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import { ownerLogin } from '../../api/owner.js';
import { useOwnerSession } from '../../stores/owner.js';

export function OwnerLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  // Mostrato solo dopo che il cloud richiede il 2FA (totp_required).
  const [totpRequired, setTotpRequired] = useState(false);
  const navigate = useNavigate();
  const setOwner = useOwnerSession((s) => s.setOwner);

  const mutation = useMutation({
    mutationFn: ownerLogin,
    onSuccess: (res) => {
      setOwner(res.owner);
      navigate('/owner', { replace: true });
    },
    onError: (err) => {
      // Se il cloud richiede il TOTP, sblocca il campo codice e lascia
      // l'utente reinviare. Non e' un errore "finale".
      if (err instanceof ApiError && err.code === 'totp_required') {
        setTotpRequired(true);
      }
    },
  });

  // Errore locale (pre-submit): es. TOTP richiesto ma codice non a 6 cifre.
  // Distinto dall'errore della mutation (cloud).
  const [localError, setLocalError] = useState<string | null>(null);
  const trimmedTotp = totpCode.trim();
  // Quando il 2FA e' richiesto, il submit e' valido solo con un codice a 6 cifre.
  const totpReady = !totpRequired || /^\d{6}$/.test(trimmedTotp);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Guard: se il cloud ha gia' richiesto il TOTP ma il campo e' vuoto/incompleto,
    // NON inviare (eviterebbe un nuovo loop 'totp_required').
    if (totpRequired && !/^\d{6}$/.test(trimmedTotp)) {
      setLocalError('Inserisci il codice a 6 cifre.');
      return;
    }
    setLocalError(null);
    mutation.mutate({
      email: email.trim().toLowerCase(),
      password,
      ...(totpRequired && trimmedTotp ? { totpCode: trimmedTotp } : {}),
    });
  };

  const errMsg =
    localError ?? (mutation.error ? mapOwnerLoginError(mutation.error, totpRequired) : null);

  return (
    <main className="min-h-screen flex items-center justify-center p-5 bg-gradient-to-br from-bg-base to-bg-muted text-text-primary font-sans antialiased">
      <div className="w-full max-w-[400px]">
        <div className="text-center mb-7">
          <div className="w-16 h-16 mx-auto mb-3.5 rounded-2xl bg-accent-primary text-text-on-accent flex items-center justify-center text-2xl font-bold shadow-[0_8px_24px_rgba(233,123,61,0.25)]">
            HM
          </div>
          <div className="text-[22px] font-bold tracking-hm-tight">Hotel Manager</div>
          <div className="text-[13px] text-text-secondary mt-1">Area Proprietario</div>
        </div>

        <form
          onSubmit={onSubmit}
          aria-label="owner-login-form"
          className="bg-bg-card rounded-[20px] p-7 border border-border-base shadow-[0_12px_40px_rgba(61,47,36,0.1),0_4px_12px_rgba(61,47,36,0.04)] flex flex-col gap-4"
        >
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium text-text-secondary">Email</span>
            <input
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={mutation.isPending}
              className="h-11 px-3 rounded-lg border border-border-input bg-bg-base text-text-primary text-base outline-none focus:border-accent-primary focus:ring-2 focus:ring-accent-soft transition-colors"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium text-text-secondary">Password</span>
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={mutation.isPending}
              className="h-11 px-3 rounded-lg border border-border-input bg-bg-base text-text-primary text-base outline-none focus:border-accent-primary focus:ring-2 focus:ring-accent-soft transition-colors"
            />
          </label>

          {totpRequired && (
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium text-text-secondary">
                Codice di verifica (2FA)
              </span>
              <input
                type="text"
                name="totpCode"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                placeholder="000000"
                required
                value={totpCode}
                onChange={(e) => {
                  setTotpCode(e.target.value.replace(/\D/g, ''));
                  setLocalError(null);
                }}
                disabled={mutation.isPending}
                className="h-11 px-3 rounded-lg border border-border-input bg-bg-base text-text-primary text-xl tracking-[0.4em] text-center font-semibold outline-none focus:border-accent-primary focus:ring-2 focus:ring-accent-soft transition-colors"
              />
            </label>
          )}

          {errMsg && (
            <p role="alert" className="text-[13px] font-medium text-state-guas m-0">
              {errMsg}
            </p>
          )}

          <button
            type="submit"
            disabled={mutation.isPending || !totpReady}
            className="h-12 rounded-lg bg-accent-primary text-text-on-accent font-semibold text-base shadow-hm-sm transition-all hover:bg-accent-primary-hover active:scale-[0.99] disabled:opacity-50 disabled:pointer-events-none"
          >
            {mutation.isPending ? 'Accesso in corso…' : 'ACCEDI'}
          </button>
        </form>

        <div className="text-center mt-6 text-[11px] text-text-muted">
          <span className="font-semibold text-text-secondary">Hotel Manager</span> · La Marca Hotels
        </div>
      </div>
    </main>
  );
}

function mapOwnerLoginError(err: unknown, totpRequired: boolean): string | null {
  if (err instanceof ApiError) {
    if (err.code === 'totp_required') {
      // Non e' un errore "finale": mostriamo solo l'invito a inserire il codice.
      return 'Inserisci il codice di verifica a 6 cifre.';
    }
    if (err.code === 'totp_invalid') return 'Codice di verifica non valido.';
    if (err.code === 'invalid_credentials' || err.status === 401) {
      return 'Email o password non validi.';
    }
    if (err.status === 429) return 'Troppi tentativi. Riprova tra qualche minuto.';
    if (err.status === 400) return 'Dati non validi.';
  }
  // Se il primo tentativo ha gia' sbloccato il TOTP, evita un messaggio
  // di connessione fuorviante.
  return totpRequired ? null : 'Errore di connessione. Riprova.';
}
